import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { invoicesAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDate, formatDateTime } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  FileText,
  Download,
  Send,
  DollarSign,
  Receipt,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  CLINIC_NAME, CLINIC_ADDRESS, CLINIC_EMAIL,
  BUSINESS_PHONES_INLINE,
} from '../config/clinicContacts';

const LOGO_URL = "/logo.png"; // Local logo file to avoid CORS issues

// Helper to load image as base64
const loadImageAsBase64 = async (url) => {
  try {
    const absoluteUrl = url.startsWith('/') ? window.location.origin + url : url;
    const response = await fetch(absoluteUrl, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load logo:', error);
    return null;
  }
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  
  const [patient, setPatient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState(patientId ? 'create' : 'list');
  
  // New invoice form
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, adjustment: 0 }
  ]);
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');

  const fetchData = async () => {
    try {
      if (patientId) {
        const [patientRes, invoicesRes] = await Promise.all([
          patientsAPI.getById(patientId),
          invoicesAPI.getAll({ patient_id: patientId }),
        ]);
        setPatient(patientRes.data);
        setInvoices(invoicesRes.data);
      } else {
        const invoicesRes = await invoicesAPI.getAll({});
        setInvoices(invoicesRes.data);
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [patientId]);

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, adjustment: 0 }]);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setLineItems(updated);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalAdjustments = 0;
    
    lineItems.forEach(item => {
      subtotal += item.quantity * item.unit_price;
      totalAdjustments += item.adjustment;
    });
    
    return {
      subtotal,
      totalAdjustments,
      total: subtotal - totalAdjustments
    };
  };

  const handleCreateInvoice = async () => {
    if (!lineItems.some(item => item.description && item.unit_price > 0)) {
      toast.error('Please add at least one valid line item');
      return;
    }

    setSaving(true);
    try {
      const response = await invoicesAPI.create({
        patient_id: patientId,
        line_items: lineItems.filter(item => item.description),
        notes,
        due_date: dueDate || null,
      });
      
      toast.success('Invoice created successfully!');
      setLineItems([{ description: '', quantity: 1, unit_price: 0, adjustment: 0 }]);
      setNotes('');
      setDueDate('');
      fetchData();
      setActiveView('list');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
    setSaving(false);
  };

  const generatePDF = async (invoice) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Try to add logo
    try {
      const logoBase64 = await loadImageAsBase64(LOGO_URL);
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', 15, 8, 25, 25);
      }
    } catch (e) {
      console.warn('Could not load logo for PDF');
    }
    
    // Header with clinic info
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(CLINIC_NAME, 45, 18);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(CLINIC_ADDRESS, 45, 24);
    doc.text(`Tel: ${BUSINESS_PHONES_INLINE}`, 45, 29);
    doc.text(`Email: ${CLINIC_EMAIL}`, 45, 34);
    
    // Invoice title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('INVOICE', pageWidth - 15, 18, { align: 'right' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.invoice_number, pageWidth - 15, 25, { align: 'right' });
    
    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(15, 40, pageWidth - 15, 40);
    
    // Patient details
    let yPos = 50;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Bill To:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(invoice.patient_name, 20, yPos + 7);
    if (invoice.patient_address) {
      doc.text(invoice.patient_address, 20, yPos + 14);
    }
    
    // Invoice details
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Invoice Date:', pageWidth - 70, yPos);
    doc.text('Due Date:', pageWidth - 70, yPos + 7);
    doc.text('Status:', pageWidth - 70, yPos + 14);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(formatDate(invoice.created_at), pageWidth - 15, yPos, { align: 'right' });
    doc.text(invoice.due_date ? formatDate(invoice.due_date) : 'Upon Receipt', pageWidth - 15, yPos + 7, { align: 'right' });
    doc.text(invoice.status.toUpperCase(), pageWidth - 15, yPos + 14, { align: 'right' });
    
    // Line items table
    const tableData = invoice.line_items.map(item => [
      item.description,
      item.quantity.toString(),
      `$${item.unit_price.toFixed(2)}`,
      item.adjustment > 0 ? `-$${item.adjustment.toFixed(2)}` : '-',
      `$${item.total.toFixed(2)}`
    ]);
    
    doc.autoTable({
      startY: 85,
      head: [['Description', 'Qty', 'Unit Price', 'Adjustment', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [15, 23, 42], fontSize: 9 },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 30, halign: 'right' },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' },
      },
      margin: { left: 15, right: 15 },
    });
    
    // Totals
    const finalY = doc.lastAutoTable.finalY + 10;
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('Subtotal:', pageWidth - 70, finalY);
    doc.text(`$${invoice.subtotal.toFixed(2)}`, pageWidth - 15, finalY, { align: 'right' });
    
    doc.text('Adjustments:', pageWidth - 70, finalY + 7);
    doc.text(`-$${invoice.total_adjustments.toFixed(2)}`, pageWidth - 15, finalY + 7, { align: 'right' });
    
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 70, finalY + 12, pageWidth - 15, finalY + 12);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text('Total Due:', pageWidth - 70, finalY + 20);
    doc.text(`$${invoice.total.toFixed(2)}`, pageWidth - 15, finalY + 20, { align: 'right' });
    
    // Notes
    if (invoice.notes) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Notes:', 20, finalY + 35);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(invoice.notes, 20, finalY + 42, { maxWidth: pageWidth - 40 });
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Thank you for choosing ${CLINIC_NAME}`, pageWidth / 2, 275, { align: 'center' });
    doc.text('Payment can be made via cash, card, or bank transfer', pageWidth / 2, 280, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
    
    // Save
    doc.save(`Invoice_${invoice.invoice_number}.pdf`);
    toast.success('Invoice PDF downloaded');
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-600 border border-slate-200',
      sent: 'bg-blue-50 text-blue-600 border border-blue-200',
      paid: 'bg-emerald-50 text-emerald-600 border border-emerald-200',
      cancelled: 'bg-red-50 text-red-600 border border-red-200',
    };
    return styles[status] || 'bg-slate-100 text-slate-600 border border-slate-200';
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn p-2" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {patientId && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(`/patients/${patientId}`)} 
              className="rounded-xl hover:bg-slate-50"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div>
            <p className="text-sm font-medium text-primary mb-1">Billing</p>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              {patientId ? 'Patient Invoices' : 'All Invoices'}
            </h1>
            {patient && (
              <p className="text-slate-500 mt-1">
                {patient.first_name} {patient.last_name} ({patient.patient_id})
              </p>
            )}
          </div>
        </div>
        
        {patientId && hasPermission('invoices:create') && (
          <div className="flex gap-3">
            <Button
              onClick={() => setActiveView('create')}
              className={cn(
                "gap-2 rounded-full transition-all",
                activeView === 'create' 
                  ? "btn-primary-glow" 
                  : "bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200"
              )}
            >
              <Plus className="h-4 w-4" />
              New Invoice
            </Button>
          </div>
        )}
      </div>

      {/* Create Invoice View */}
      {activeView === 'create' && patientId && (
        <div className="premium-card overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Create Invoice</h2>
            <p className="text-sm text-slate-500">Add services and generate an invoice for this encounter</p>
          </div>
          <div className="p-6 space-y-6">
            {/* Line Items */}
            <div>
              <Label className="mb-3 block text-xs font-semibold uppercase tracking-wider text-slate-500">Line Items</Label>
              <div className="space-y-3">
                {lineItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-5">
                      <Input
                        placeholder="Service description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="rounded-xl bg-white border-slate-200"
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        className="rounded-xl bg-white border-slate-200"
                        data-testid={`item-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Price"
                        min="0"
                        step="0.01"
                        value={item.unit_price || ''}
                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                        className="rounded-xl bg-white border-slate-200"
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Discount"
                        min="0"
                        step="0.01"
                        value={item.adjustment || ''}
                        onChange={(e) => updateLineItem(index, 'adjustment', e.target.value)}
                        className="rounded-xl bg-white border-slate-200"
                        data-testid={`item-adjustment-${index}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                        className="rounded-xl hover:bg-red-500/10 text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                variant="ghost" 
                onClick={addLineItem} 
                className="mt-3 gap-2 rounded-full bg-slate-50 hover:bg-slate-100"
              >
                <Plus className="h-4 w-4" />
                Add Line Item
              </Button>
            </div>

            {/* Totals */}
            <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
              <div className="flex justify-between text-sm mb-2 text-slate-500">
                <span>Subtotal</span>
                <span className="text-slate-900">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2 text-slate-500">
                <span>Adjustments</span>
                <span className="text-red-600">-${totals.totalAdjustments.toFixed(2)}</span>
              </div>
              <Separator className="my-3 bg-slate-100" />
              <div className="flex justify-between font-bold text-lg">
                <span className="text-slate-900">Total</span>
                <span className="text-primary">${totals.total.toFixed(2)}</span>
              </div>
            </div>

            {/* Additional Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="rounded-xl bg-white border-slate-200"
                  data-testid="due-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes or payment instructions..."
                rows={3}
                className="rounded-xl bg-white border-slate-200"
                data-testid="notes-input"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button 
                onClick={handleCreateInvoice} 
                disabled={saving} 
                className="gap-2 rounded-full btn-primary-glow" 
                data-testid="create-invoice-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                Create Invoice
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setActiveView('list')}
                className="rounded-full hover:bg-slate-50"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice List */}
      {(activeView === 'list' || !patientId) && (
        <div className="premium-card overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">
              {invoices.length} Invoice{invoices.length !== 1 ? 's' : ''}
            </h2>
          </div>
          <div className="p-0">
            {invoices.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                  <Receipt className="h-8 w-8 text-slate-500" strokeWidth={1.5} />
                </div>
                <p className="text-slate-500">No invoices found</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <table className="premium-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
                      {!patientId && <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Patient</th>}
                      <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                      <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Total</th>
                      <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="px-5 py-4 w-[100px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`invoice-row-${invoice.id}`}>
                        <td className="px-5 py-4">
                          <code className="text-sm bg-slate-50 px-2.5 py-1 rounded-lg font-mono text-primary">
                            {invoice.invoice_number}
                          </code>
                        </td>
                        {!patientId && (
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2 text-slate-900">
                              <User className="h-4 w-4 text-slate-500" />
                              {invoice.patient_name}
                            </div>
                          </td>
                        )}
                        <td className="px-5 py-4 text-slate-500">{formatDate(invoice.created_at)}</td>
                        <td className="px-5 py-4">
                          <span className="font-semibold text-slate-900">${invoice.total.toFixed(2)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <Badge className={cn("rounded-lg", getStatusBadge(invoice.status))}>
                            {invoice.status}
                          </Badge>
                        </td>
                        <td className="px-5 py-4">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => generatePDF(invoice)}
                            className="rounded-xl hover:bg-slate-50"
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
